import ManualPZTransportPicker from '../modules/warehouse/ManualPZTransportPicker';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  App,
  Form,
  Input,
  Button,
  DatePicker,
  Dropdown,
  Select,
  Spin,
  Popconfirm,
  InputNumber,
  Descriptions,
  Alert,
  Tooltip
} from 'antd';
import type { MenuProps } from 'antd';
import {
  CalculatorOutlined,
  CalendarOutlined,
  CarOutlined,
  EditOutlined,
  SaveOutlined,
  FilePdfOutlined,
  MoreOutlined,
  PlusOutlined,
  FileTextOutlined,
  ShopOutlined,
  InboxOutlined,
  CloseOutlined,
  UndoOutlined,
  InfoCircleOutlined,
  StopOutlined,
  UnorderedListOutlined,
  UserOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import WarehouseCorrectionPanel from '../modules/warehouse/WarehouseCorrectionPanel';
import PZCorrectionReceiptDatePreview from '../modules/warehouse/PZCorrectionReceiptDatePreview';
import { authService } from '../services/auth';
import { normalizePZDocument, PZDocumentDto } from '../dto/pz';
import { canManagePZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import BlockedActionsAlert from '../modules/warehouse/BlockedActionsAlert';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import {
  buildPZItemsPayload,
  hasPZItemsMissingReceivePrice,
  isPZReceiptQuantityOnly,
  validatePZItemsForSave,
} from '../modules/warehouse/pzItemPayload';
import { askWarehouseSaveIntent } from '../modules/warehouse/warehouseSaveIntent';
import {
  receivePZDocument,
  type PZInventoryProcessingSummary,
} from '../modules/warehouse/pzReceiveFlow';
import { buildPZVariantAutofill } from '../modules/warehouse/pzVariantSelection';
import {
  WarehouseQuantityPatch,
  extractWarehouseQuantitySetPatch,
  formatWarehouseQuantityTotals,
  reexpressWarehouseSubquantity,
  rebaseWarehouseUnitPrice,
} from '../modules/warehouse/warehouseQuantityConversion';
import dayjs from 'dayjs';
import { formatCurrency } from '../utils/currency';
import { calculatePZDocumentTotal } from '../utils/pzTotals';
import { PZItem, PZStatus, UpdatePZDto, Supplier, ProductVariant } from '../types/pz';
import { getPZErrorMessage, isBlockingPZError, PZ_ERROR_CODE_KEYS } from '../modules/warehouse/pzErrorMessages';
import { useFeedback } from '../utils/feedback';
import { shouldPreventEnterFormSubmit } from '../utils/formEnterGuard';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { fetchSupplierOrderTransports } from '../modules/orders/api/orderTransportApi';
import {
  mapTransportSummaryToSupplierOrderTransport,
  type SupplierOrderTransportDto,
} from '../dto/transport';
import DeliveryTransportPicker from '../modules/transports/components/DeliveryTransportPicker';
import { formatUnit, normalizeUnitCode } from '../utils/units';
import { formatQuantitySet } from '../utils/commercialQuantity';

import PZItemsSection, { type PZItemRow } from './PZItemsSection';
import PZReceiptValue from './PZReceiptValue';
import { getPZOperationalStatus } from '../modules/warehouse/warehouseDocumentOperationalStatus';
import PZBatchConversionMenu from '../modules/warehouse/components/PZBatchConversionMenu';
import {
  convertPZItemsToTarget,
  type PZBatchConversionTarget,
} from '../modules/warehouse/pzBatchQuantityConversion';
import PZStornoDialog from '../modules/warehouse/PZStornoDialog';
import {
  loadPZStornoPreview,
  type PZStornoPreview,
} from '../modules/warehouse/pzStornoApi';
import { useBackNavigation } from '../hooks/useBackNavigation';
import {
  ObjectCardFacts,
  ObjectCardHero,
  ObjectCardSection,
  type ObjectCardFact,
} from '../components/layout/ObjectCard';
import { layoutCardSlots, type CardSlot } from '../components/layout/DocumentCard';
import './PZDetail.css';

const { Option } = Select;
const { TextArea } = Input;
const EMPTY_VALUE = '—';


const PZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const location = useLocation();
  const { t, i18n } = useTranslation(['pz', 'common', 'orders', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();

  const queryParams = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pzDocument, setPzDocument] = useState<PZDocumentDto | null>(null);
  const [items, setItems] = useState<PZItemRow[]>([
    { key: '0', productVariantId: '', quantityOrdered: 1, unitPrice: undefined, currency: 'PLN' }
  ]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryInfo, setInventoryInfo] = useState<PZInventoryProcessingSummary | null>(null);
  const [itemValidationError, setItemValidationError] = useState<string | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const statusChangeInFlightRef = useRef(false);
  // Storno (owner decision 2026-09-18): fetched only for a RECEIVED document,
  // so the "Stornuj przyjęcie" button starts disabled and only enables once
  // we know whether the batch was already issued/reserved elsewhere.
  const [stornoPreview, setStornoPreview] = useState<PZStornoPreview | null>(null);
  const [stornoPreviewLoading, setStornoPreviewLoading] = useState(false);
  const [stornoDialogOpen, setStornoDialogOpen] = useState(false);
  // Transports linked to THIS document. Kept in component state (not in the
  // antd Form) for the same reason `items` is: the value is a list the operator
  // edits through a dedicated control, and `handleSave` assembles the payload
  // explicitly, so it never needs to round-trip through form fields.
  const [sourceTransportIds, setSourceTransportIds] = useState<string[]>([]);
  const [supplierOrderTransports, setSupplierOrderTransports] = useState<SupplierOrderTransportDto[]>([]);
  const [supplierOrderTransportsLoading, setSupplierOrderTransportsLoading] = useState(false);
  const [supplierOrderTransportsFailed, setSupplierOrderTransportsFailed] = useState(false);
  // Which supplier order (ZD) this receipt belongs to, and therefore which
  // trucks the transport picker may offer. The document itself is the
  // authoritative answer, so the section survives a cold reopen. A manual PZ
  // has none, and correctly gets no picker.
  const sourceSupplierOrderId = pzDocument?.sourceSupplierOrderId || null;

  // Operator-facing identity of THIS document, used by every confirmation toast
  // so a success never reads as a bare "Zapisano". Empty only until the document
  // has loaded, when the toasts that use it cannot fire yet.
  const documentNumber = pzDocument?.pzNumber || '';

  const currentUser = authService.getCurrentUser();
  const canEdit = canManagePZDocuments(currentUser);
  const canViewPZPurchasePrices = authService.canViewWarehouseDocumentPurchasePrices();
  const canEditPurchasePrices = authService.canEditPZDocumentPurchasePrices();
  const currentUserDisplayName = currentUser?.name || '';
  const isCorrection = pzDocument?.status === PZStatus.RECEIVED;
  // Storno (owner decision 2026-09-18): distinguishes a stornoed receipt from
  // an ordinary cancelled draft — both share status CANCELLED, but only the
  // storno path stamps stornoAt.
  const isStorno = pzDocument?.status === PZStatus.CANCELLED && Boolean(pzDocument?.stornoAt);
  const invoiceBacked = Boolean(pzDocument?.purchaseInvoiceId);
  // An invoice-backed PZ stays editable before it is received (WP1, decision
  // 2026-09-24): header fields only — its lines, prices, currency and dates
  // come from the invoice (backend PZ_INVOICE_FIELDS_LOCKED), so the line
  // section stays read-only. Once received it uses the correction mode.
  const invoiceLinesLocked = invoiceBacked && !isCorrection;
  const isEditMode = (canEdit && Boolean(pzDocument) && (
    (queryParams.get('mode') === 'edit' && !(invoiceBacked && isCorrection))
    || (queryParams.get('mode') === 'correction' && isCorrection)
  ));
  // Manual receipt (no ZD, no invoice): one quantity field, saved as both the
  // line quantity and the received quantity. Decided from the persisted
  // document, never from the lines being edited, so it cannot flip mid-edit.
  const receiptQuantityOnly = isPZReceiptQuantityOnly({
    sourceSupplierOrderId,
    purchaseInvoiceId: pzDocument?.purchaseInvoiceId,
    persistedItems: pzDocument?.items,
  });
  const canEditQuantityReceived = pzDocument?.status === PZStatus.DRAFT
    || pzDocument?.status === PZStatus.CONFIRMED
    || isCorrection;
  // Backend-computed reasons (GET /pz/:id → blockedActions). A button whose
  // action is listed stays visible but disabled, with the reason as tooltip;
  // the page never re-derives the status rules itself.
  const blockedActions = pzDocument?.blockedActions || [];
  const blockedFor = (action: string) => findBlockedAction(blockedActions, action);
  const blockedTooltip = (action: string) => {
    const blocked = blockedFor(action);
    return blocked ? getBlockedActionMessage(blocked, t, 'pz', PZ_ERROR_CODE_KEYS, { siblings: blockedActions }) : undefined;
  };

  const fetchPZDocument = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pz/${id}`);
      const doc = normalizePZDocument(response.data);
      setPzDocument(doc);
      // Etap 2: the transport links round-trip through component state, exactly
      // like `items` below — the fields stripped from `formValues` further down
      // stay stripped on purpose (they are not form inputs).
      setSourceTransportIds(doc.sourceTransportIds || []);
      const formValues: Record<string, unknown> = { ...doc };
      [
        'sourceTransportId',
        'sourceTransportIds',
        'sourceTransports',
        'transportCompany',
        'transportInvoiceNo',
        'transportCost',
        'transportCurrency',
        'vehicleRegistration',
        'driverName',
        'driverPhone',
        'allocateTransportCost',
      ].forEach((field) => {
        delete formValues[field];
      });

      // Set form values
      form.setFieldsValue({
        correctionReason: undefined,
        ...formValues,
        receiptDate: dayjs(doc.receiptDate),
        // Historical PZ payloads can lack issueDate even though a PZ still
        // needs to remain editable. Keep their established receipt date as a
        // one-time form fallback; every subsequent save persists it explicitly.
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : dayjs(doc.receiptDate),
        expectedDeliveryDate: doc.expectedDeliveryDate ? dayjs(doc.expectedDeliveryDate) : undefined,
        supplierInvoiceDate: doc.supplierInvoiceDate ? dayjs(doc.supplierInvoiceDate) : undefined,
        supplierId: doc.supplierId,
        approvedBy: doc.approvedBy,
        receivedBy: doc.receivedBy,
      });

      // Set items
      if (doc.itemsLoaded) {
        setItems(doc.items.map((item: PZItem, index: number) => ({
          ...item,
          key: item.id || index.toString()
        })));
      }
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.fetchDetailFailed'));
      navigate('/app/pz');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, t, feedback]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers?isActive=true');
      setSuppliers(response.data);
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      feedback.error(t('pz:error.suppliersFetchFailed'));
    }
  }, [t, feedback]);

  useEffect(() => {
    fetchSuppliers();
    fetchPZDocument();
  }, [fetchPZDocument, fetchSuppliers]);

  useEffect(() => {
    if (!canEdit || !pzDocument || pzDocument.status !== PZStatus.RECEIVED) {
      setStornoPreview(null);
      return;
    }
    let active = true;
    setStornoPreviewLoading(true);
    loadPZStornoPreview(pzDocument.id)
      .then((preview) => {
        if (active) setStornoPreview(preview);
      })
      .catch(() => {
        if (active) setStornoPreview(null);
      })
      .finally(() => {
        if (active) setStornoPreviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canEdit, pzDocument]);

  // Etap 2: candidate trucks for a saved document. With a known ZD scope the
  // operator sees every transport on that supplier order; without it the list
  // narrows to the ones already on this PZ (still enough to review or unlink).
  const loadSupplierOrderTransports = useCallback(async () => {
    if (!sourceSupplierOrderId) {
      return;
    }

    setSupplierOrderTransportsLoading(true);
    setSupplierOrderTransportsFailed(false);
    try {
      setSupplierOrderTransports(await fetchSupplierOrderTransports(sourceSupplierOrderId));
    } catch (error) {
      setSupplierOrderTransports([]);
      setSupplierOrderTransportsFailed(true);
    } finally {
      setSupplierOrderTransportsLoading(false);
    }
  }, [sourceSupplierOrderId]);

  useEffect(() => {
    if (!sourceSupplierOrderId) {
      return;
    }

    void loadSupplierOrderTransports();
  }, [sourceSupplierOrderId, loadSupplierOrderTransports]);

  const linkedTransportOptions = (pzDocument?.sourceTransports || [])
    .map(mapTransportSummaryToSupplierOrderTransport);
  // Union, candidates first: a transport already on this PZ must stay visible
  // even when the candidate fetch failed or the link predates the current
  // supplier-order allocations.
  const deliveryTransportOptions = [
    ...supplierOrderTransports,
    ...linkedTransportOptions.filter(
      (linked) => !supplierOrderTransports.some((candidate) => candidate.id === linked.id),
    ),
  ];
  const canEditManualDeliveryTransports = !sourceSupplierOrderId && !pzDocument?.purchaseInvoiceId
    && isEditMode && canEdit
    && (pzDocument?.status === PZStatus.DRAFT || pzDocument?.status === PZStatus.CONFIRMED);
  const canEditDeliveryTransports = !pzDocument?.purchaseInvoiceId && isEditMode
    && canEdit
    && (pzDocument?.status === PZStatus.DRAFT || pzDocument?.status === PZStatus.CONFIRMED)
    && (Boolean(sourceSupplierOrderId) || linkedTransportOptions.length > 0);
  const showDeliveryTransportsSection = !canEditManualDeliveryTransports && !pzDocument?.purchaseInvoiceId
    && (linkedTransportOptions.length > 0 || canEditDeliveryTransports);

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      form.setFieldsValue({
        supplierName: supplier.name,
        supplierAddress: supplier.address || '',
        supplierNIP: supplier.taxId || '',
      });
    }
  };

  /**
   * Closes a successful save: either that was all the operator wanted (DRAFT),
   * or the goods go on stock right away (POST). A failed receipt is not a
   * failed save — the document exists as a draft, so the operator lands ON it
   * with the sticky reason and retries from "Przyjmij towar wirtualnie".
   */
  const finishSaveWithIntent = async (
    saveIntent: 'DRAFT' | 'POST',
    savedId: string | undefined,
    savedNumber: string | undefined,
  ) => {
    if (saveIntent === 'POST' && savedId) {
      const { ok } = await receivePZDocument({
        pzId: savedId,
        pzNumber: savedNumber,
        t,
        feedback,
        documentSaved: true,
        modal,
      });
      if (!ok) {
        navigate(`/app/pz/${savedId}`, { replace: true });
        return;
      }
    }
    navigate('/app/pz');
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      if (pzDocument && !pzDocument.itemsLoaded) {
        feedback.error(t('pz:validation.itemsNotLoaded'), { sticky: true });
        setSaving(false);
        return;
      }

      const documentCurrency = values.currency || values.supplierInvoiceCurrency || 'PLN';
      const itemValidationKey = validatePZItemsForSave(items, { canEditPurchasePrices, receiptQuantityOnly });
      if (itemValidationKey) {
        const validationMessage = t(`pz:validation.${itemValidationKey}`);
        setItemValidationError(validationMessage);
        feedback.error(validationMessage);
        setSaving(false);
        return;
      }
      setItemValidationError(null);
      const itemPayload = buildPZItemsPayload(items, {
        canEditPurchasePrices,
        documentCurrency,
        receiptQuantityOnly,
      });

      // Validate items
      if (itemPayload.length === 0) {
        feedback.error(t('pz:validation.itemsRequired'));
        setSaving(false);
        return;
      }

      // Saving a PZ is a two-part decision (save + put the goods on stock?), so
      // it is asked once, here, instead of sending the operator back into the
      // saved document for a second click. A document that cannot be received
      // yet (line without a purchase price) offers the draft exit only.
      const saveIntent = isCorrection ? 'DRAFT' : await askWarehouseSaveIntent({
        documentType: 'PZ',
        modal,
        t,
        postBlockedReason: hasPZItemsMissingReceivePrice(items)
          ? t('pz:validation.priceRequiredBeforeStatus')
          : null,
      });
      if (!saveIntent) {
        setSaving(false);
        return;
      }

      const data: UpdatePZDto = {
        ...(isCorrection ? {
          correctionReason: values.correctionReason?.trim(),
          expectedUpdatedAt: pzDocument?.updatedAt,
        } : {}),
        pzNumber: values.pzNumber?.trim() || undefined,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        receiptDate: values.receiptDate.format('YYYY-MM-DD'),
        supplierId: values.supplierId,
        currency: documentCurrency,
        supplierInvoiceNumber: canEditPurchasePrices ? values.supplierInvoiceNumber : undefined,
        supplierInvoiceDate: canEditPurchasePrices ? values.supplierInvoiceDate?.format('YYYY-MM-DD') : undefined,
        supplierInvoiceAmount: canEditPurchasePrices ? values.supplierInvoiceAmount : undefined,
        supplierInvoiceCurrency: canEditPurchasePrices ? values.supplierInvoiceCurrency || documentCurrency : undefined,
        approvedBy: values.approvedBy?.trim() || null,
        receivedBy: values.receivedBy?.trim() || null,
        // Etap 2: only sent when the operator actually had the control in front
        // of her. Sending it unconditionally would clear the links of any PZ
        // saved from a surface that never showed them.
        ...((canEditDeliveryTransports || canEditManualDeliveryTransports) ? { sourceTransportIds } : {}),
        notes: values.notes,
        items: itemPayload,
      };

      const updated = await submitWithOperationalException(
        (confirmedPayload) => api.put(`/pz/${id}`, confirmedPayload),
        data as Record<string, unknown>,
        modal,
        t,
      );
      if (!updated) return;
      const savedNumber = data.pzNumber || pzDocument?.pzNumber;
      feedback.success(savedNumber
        ? t('pz:success.updated', { pzNumber: savedNumber })
        : t('pz:success.updatedFallback'));
      await finishSaveWithIntent(saveIntent, id, savedNumber);
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getSelectedCurrency = () =>
    form.getFieldValue('currency') || form.getFieldValue('supplierInvoiceCurrency') || 'PLN';

  const syncDocumentCurrency = (currency: string) => {
    const nextCurrency = currency || 'PLN';
    form.setFieldsValue({
      currency: nextCurrency,
      supplierInvoiceCurrency: nextCurrency,
    });
    setItems((currentItems) => currentItems.map((item) => ({
      ...item,
      currency: nextCurrency,
    })));
  };

  const handleFormValuesChange = (changedValues: Record<string, unknown>) => {
    if (typeof changedValues.currency === 'string') {
      syncDocumentCurrency(changedValues.currency);
    } else if (typeof changedValues.supplierInvoiceCurrency === 'string') {
      syncDocumentCurrency(changedValues.supplierInvoiceCurrency);
    }
  };

  const handleStatusChange = async (newStatus: PZStatus) => {
    if (statusChangeInFlightRef.current) return;

    if ([PZStatus.CONFIRMED, PZStatus.RECEIVED].includes(newStatus)) {
      if (hasPZItemsMissingReceivePrice(items)) {
        const validationMessage = t('pz:validation.priceRequiredBeforeStatus');
        setItemValidationError(validationMessage);
        feedback.error(validationMessage);
        return;
      }
    }

    statusChangeInFlightRef.current = true;
    setStatusChanging(true);
    try {
      // The receipt path lives in modules/warehouse/pzReceiveFlow so this
      // button and the save-time choice stay one behavior.
      if (newStatus === PZStatus.RECEIVED) {
        const { ok, inventoryProcessing } = await receivePZDocument({
          pzId: id!,
          pzNumber: documentNumber,
          t,
          feedback,
          modal,
        });
        if (ok && inventoryProcessing) {
          setInventoryInfo((current) => {
            const next = inventoryProcessing;
            const nextHasReceipt = Number(next.batchesCreated || 0) > 0;
            const currentHasReceipt = Number(current?.batchesCreated || 0) > 0;
            return currentHasReceipt && !nextHasReceipt ? current : next;
          });
        }
        await fetchPZDocument();
        return;
      }

      await api.put(`/pz/${id}/status`, { status: newStatus });

      if (newStatus === PZStatus.CONFIRMED) {
        feedback.success(t('pz:success.confirmed', { pzNumber: documentNumber }));
      } else if (newStatus === PZStatus.CANCELLED) {
        feedback.success(t('pz:success.cancelled', { pzNumber: documentNumber }));
      } else if (newStatus === PZStatus.DRAFT) {
        feedback.success(t('pz:success.restored', { pzNumber: documentNumber }));
      }

      await fetchPZDocument();
    } catch (error: any) {
      // RECEIVED returned above — pzReceiveFlow owns that failure sentence.
      const errorKey = newStatus === PZStatus.CONFIRMED ? 'confirmFailed' :
                       newStatus === PZStatus.CANCELLED ? 'cancelFailed' :
                       newStatus === PZStatus.DRAFT ? 'restoreFailed' : 'saveFailed';
      // A blocked status transition is not a retry-and-hope failure: the
      // operator has to reload or fix the document, so the toast waits for her.
      feedback.error(getPZErrorMessage(error, t, `pz:error.${errorKey}`), {
        sticky: isBlockingPZError(error),
      });
    } finally {
      statusChangeInFlightRef.current = false;
      setStatusChanging(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!canViewPZPurchasePrices) {
      return;
    }

    try {
      const response = await api.get(`/pz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pzDocument?.pzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('pz:success.pdfDownloaded', { pzNumber: documentNumber }));
    } catch (error: any) {
      feedback.error(getPZErrorMessage(error, t, 'pz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, {
      key: Date.now().toString(),
      productVariantId: '',
      quantityOrdered: 1,
      unitPrice: undefined,
      currency: getSelectedCurrency()
    }]);
  };

  const removeItem = (key: string) => {
    setItems(items.filter(item => item.key !== key));
  };

  const updateItem = (key: string, field: keyof PZItemRow, value: any) => {
    setItemValidationError(null);
    const updatedItems = items.map(item => {
      if (item.key === key) {
        const updated = { ...item, [field]: value };

        // Quantity reaches state through updateItemQuantity only (the conversion
        // modal decides quantity and conversionUnit together); this path covers
        // the remaining editable fields.
        if (canEditPurchasePrices && field === 'unitPrice') {
          updated.totalPrice = (updated.purchaseQuantity ?? updated.quantityOrdered ?? 0) * (updated.unitPrice || 0);
        }

        return updated;
      }
      return item;
    });
    setItems(updatedItems);
  };

  /** Quantity, source unit, and conversion are one operator decision and must
   *  land in one state update. The catalog does not own a warehouse unit. */
  const updateItemQuantity = (key: string, patch: WarehouseQuantityPatch) => {
    setItemValidationError(null);
    setItems((current) => current.map((item) => {
      if (item.key !== key) return item;

      const updated: PZItemRow = {
        ...item,
        ...extractWarehouseQuantitySetPatch(patch),
        quantityOrdered: patch.quantity,
        unit: patch.unit,
        conversionUnit: patch.conversionUnit,
        conversionQuantity: patch.conversionQuantity,
        purchaseQuantity: patch.pricingQuantity ?? null,
        purchaseUnit: patch.pricingUnit ?? null,
      };
      if (normalizeUnitCode(item.unit) !== normalizeUnitCode(patch.unit)) {
        updated.quantityReceived = reexpressWarehouseSubquantity(
          item.quantityReceived,
          Number(item.quantityOrdered || 0),
          patch.quantity,
        );
        updated.quantityDamaged = reexpressWarehouseSubquantity(
          item.quantityDamaged ?? 0,
          Number(item.quantityOrdered || 0),
          patch.quantity,
        ) ?? 0;
      }
      if (canEditPurchasePrices) {
        const rebased = rebaseWarehouseUnitPrice({
          quantityPcs: item.quantityPcs,
          quantityM2: item.quantityM2,
          quantityM3: item.quantityM3,
          quantity: item.quantityOrdered,
          unit: item.unit,
          pricingQuantity: item.purchaseQuantity,
          pricingUnit: item.purchaseUnit,
          unitPrice: item.unitPrice,
          width: item.width,
          height: item.height,
          length: item.length,
          thickness: item.thickness,
        }, patch);
        updated.unitPrice = rebased.unitPrice;
        updated.totalPrice = rebased.totalValue;
      }
      return updated;
    }));
  };

  const handleBatchConversion = (target: PZBatchConversionTarget) => {
    setItemValidationError(null);
    const result = convertPZItemsToTarget(items, target, canEditPurchasePrices);
    setItems(result.items);
    if (result.convertedCount === 0) {
      feedback.warning(t('pz:batchConversion.none'));
    } else if (result.skippedCount > 0) {
      feedback.warning(t('pz:batchConversion.partial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
    } else {
      feedback.success(t('pz:batchConversion.success', { count: result.convertedCount }));
    }
  };

  // Auto-fill product info from the catalog item supplied by VariantPicker.
  // Reads the passed `variant` (not a separate fetch) so it also works for a
  // product created inline via the picker's "Add new product" flow.
  const handleVariantSelect = (key: string, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = items.map(item => {
      if (item.key !== key) return item;
      return {
        ...item,
        ...buildPZVariantAutofill(item, variantId, variant, canEditPurchasePrices),
      };
    });
    setItems(updatedItems);
  };

  const operationalStatus = pzDocument ? getPZOperationalStatus(pzDocument.status) : null;
  const statusPillClass = isStorno
    ? 'warehouse-storno-tag'
    : operationalStatus === PZStatus.CANCELLED
      ? 'obj-pill--danger'
      : operationalStatus === PZStatus.RECEIVED
        ? 'obj-pill--success'
        : operationalStatus === PZStatus.CONFIRMED ? 'obj-pill--primary' : '';
  const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY') : EMPTY_VALUE);

  // Calculate totals
  const calculateTotals = () => {
    // Quantities from different entered units cannot collapse into one figure, so
    // each unit is totalled separately (mirrors the RAZEM row on the PZ PDF).
    const totalQuantity = formatWarehouseQuantityTotals(
      items.map((item) => ({ ...item, quantity: item.quantityOrdered })),
      i18n.language,
    );
    const totalValue = canViewPZPurchasePrices
      ? calculatePZDocumentTotal(items, form.getFieldValue('currency') || 'PLN')
      : { amount: 0, currency: form.getFieldValue('currency') || 'PLN', mixedCurrency: false };
    return { totalQuantity, totalValue };
  };


  const { totalQuantity, totalValue } = calculateTotals();

  /** Warehouse operations of a saved document: the card's sticky operations bar. */
  const renderStatusActions = () => {
    if (isEditMode || !pzDocument || !canEdit) {
      return null;
    }

    // Disabled state and tooltip come from the backend blockedActions contract
    // (action 'storno'); the preview only feeds the dialog's contents.
    const stornoBlockedMessage = blockedTooltip('storno');
    const stornoDisabled = statusChanging || Boolean(blockedFor('storno'));
    const stornoButton = (
      <Button
        danger
        icon={<StopOutlined />}
        loading={stornoPreviewLoading}
        disabled={stornoDisabled}
        onClick={() => setStornoDialogOpen(true)}
      >
        {t('pz:actions.storno')}
      </Button>
    );
    const hint = t(isStorno
      ? 'pz:statusFlow.stornoInfo'
      : `pz:statusFlow.${getPZOperationalStatus(pzDocument.status).toLowerCase()}Info`);

    return (
      <div className="doc-bar" data-testid="pz-status-actions">
        <span className="doc-bar__hint" title={hint}>
          <InfoCircleOutlined aria-hidden="true" />
          <span>{hint}</span>
        </span>
        <div className="doc-bar__actions">
          {pzDocument.status !== PZStatus.CANCELLED && (
            <Tooltip title={blockedTooltip('cancel')}>
              <Popconfirm
                title={t('pz:confirm.cancelPZ')}
                onConfirm={() => { void handleStatusChange(PZStatus.CANCELLED); }}
                okText={t('common:yes')}
                cancelText={t('common:no')}
                disabled={Boolean(blockedFor('cancel'))}
              >
                <Button
                  danger
                  icon={<CloseOutlined />}
                  loading={statusChanging}
                  disabled={statusChanging || Boolean(blockedFor('cancel'))}
                >
                  {t('pz:actions.cancel')}
                </Button>
              </Popconfirm>
            </Tooltip>
          )}
          {pzDocument.status === PZStatus.RECEIVED && (
            stornoBlockedMessage ? (
              <Tooltip title={stornoBlockedMessage}>
                {/* antd disables pointer events on a disabled Button, so the
                    wrapper is what keeps the "why can't I storno this"
                    tooltip reachable — same pattern as the transport picker's
                    action cell above. */}
                <span className="doc-tooltip-anchor">{stornoButton}</span>
              </Tooltip>
            ) : stornoButton
          )}
          {pzDocument.status === PZStatus.CANCELLED && !isStorno && (
            <Popconfirm
              title={t('pz:confirm.restorePZ')}
              description={t('pz:confirm.restorePZDescription')}
              onConfirm={() => { void handleStatusChange(PZStatus.DRAFT); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button icon={<UndoOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.restore')}
              </Button>
            </Popconfirm>
          )}
          {/* Not hidden on a cancelled PZ: the backend lists `receive` as blocked and
              the button stays visible, disabled, with the reason (see above). */}
          {pzDocument.status !== PZStatus.RECEIVED && (
            <Tooltip title={blockedTooltip('receive')}>
              <Popconfirm
                title={t('pz:confirm.receivePZ')}
                description={t('pz:confirm.receivePZDescription')}
                onConfirm={() => { void handleStatusChange(PZStatus.RECEIVED); }}
                okText={t('pz:actions.receiveVirtual')}
                cancelText={t('common:no')}
                disabled={Boolean(blockedFor('receive'))}
              >
                <Button
                  type="primary"
                  icon={<InboxOutlined />}
                  loading={statusChanging}
                  disabled={statusChanging || Boolean(blockedFor('receive'))}
                >
                  {t('pz:actions.receiveVirtual')}
                </Button>
              </Popconfirm>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="obj-loading">
        <Spin size="large" />
      </div>
    );
  }

  const moreItems: NonNullable<MenuProps['items']> = canViewPZPurchasePrices
    ? [{ key: 'pdf', icon: <FilePdfOutlined />, label: t('pz:actions.downloadPDF') }]
    : [];

  const facts: ObjectCardFact[] = [
    { key: 'supplier', label: t('pz:fields.supplier'), icon: <ShopOutlined />, value: pzDocument?.supplierName || EMPTY_VALUE, testId: 'pz-chip-supplier' },
    { key: 'receiptDate', label: t('pz:fields.receiptDate'), icon: <CalendarOutlined />, value: formatDate(pzDocument?.receiptDate), testId: 'pz-chip-receipt-date' },
    { key: 'issueDate', label: t('pz:fields.issueDate'), icon: <CalendarOutlined />, value: formatDate(pzDocument?.issueDate || pzDocument?.receiptDate), testId: 'pz-chip-issue-date' },
    { key: 'currency', label: t('pz:fields.currency'), icon: <WalletOutlined />, value: pzDocument?.currency || 'PLN', testId: 'pz-chip-currency' },
    ...(canViewPZPurchasePrices ? [{
      key: 'supplierInvoice',
      label: t('pz:sections.supplierInvoice'),
      icon: <FileTextOutlined />,
      value: pzDocument?.supplierInvoiceNumber || EMPTY_VALUE,
      testId: 'pz-chip-supplier-invoice',
    }] : []),
  ];

  const stockValuation = pzDocument?.stockValuation;
  const headerAlerts = [
    inventoryInfo && (
      <Alert
        key="inventory"
        message={t('pz:inventoryInfo.title')}
        description={
          <Descriptions column={2} size="small">
            <Descriptions.Item label={t('pz:inventoryInfo.batchCreated')}>
              {inventoryInfo.batchesCreated}
            </Descriptions.Item>
            <Descriptions.Item label={t('pz:fields.totalQuantity')}>
              {formatQuantitySet(
                inventoryInfo as unknown as Record<string, unknown>,
                'totalQuantity',
                i18n.language,
                {
                  fallbackQuantity: inventoryInfo.totalQuantity,
                  fallbackUnit: inventoryInfo.totalQuantityUnit,
                },
              )}
            </Descriptions.Item>
            {canViewPZPurchasePrices && (
              <Descriptions.Item label={t('pz:inventoryInfo.totalCostPLN')}>
                {formatCurrency(inventoryInfo.totalCostPLN, 'PLN')}
              </Descriptions.Item>
            )}
          </Descriptions>
        }
        type="success"
        showIcon
        icon={<InboxOutlined />}
        closable
        onClose={() => setInventoryInfo(null)}
      />
    ),
    pzDocument?.ownStockCostsIgnored && (
      <Alert key="own-stock" type="warning" showIcon message={t('pz:ownStockCostsIgnored')} />
    ),
    invoiceLinesLocked && isEditMode && (
      <Alert key="invoice-lines" type="info" showIcon message={t('pz:invoiceWorkflow.linesFromInvoice')} />
    ),
    pzDocument?.withdrawnPurchaseInvoice && (
      <Alert key="withdrawn" type="warning" showIcon data-testid="pz-withdrawn-invoice"
        message={t('pz:invoiceWorkflow.withdrawnInvoice', { number: pzDocument.withdrawnPurchaseInvoice.number || '' })} />
    ),
    pzDocument?.purchaseInvoiceId && (
      <Alert key="received" type="success" showIcon
        message={t('pz:invoiceWorkflow.received', { number: pzDocument.supplierInvoiceNumber || '' })}
        action={authService.canManagePurchaseInvoiceImports()
          ? <Button size="small" onClick={() => navigate(`/app/purchase-invoices/${pzDocument.purchaseInvoiceId}`)}>{t('pz:invoiceWorkflow.openInvoice')}</Button>
          : undefined} />
    ),
    pzDocument?.invoicePending && (
      <Alert key="pending" type="warning" showIcon
        message={t('pz:manualReceipt.title')} description={t('pz:manualReceipt.hint')} />
    ),
    isCorrection && isEditMode && id && pzDocument && (
      <PZCorrectionReceiptDatePreview
        key="receipt-date"
        documentId={id}
        currentReceiptDate={pzDocument.receiptDate}
        currency={pzDocument.currency}
      />
    ),
  ].filter(Boolean);

  const itemsSection: CardSlot = {
    key: 'items',
    wide: true,
    render: () => (
      <ObjectCardSection
        wide
        testId="pz-items-section"
        tone="blue"
        icon={<UnorderedListOutlined />}
        title={t('pz:sections.items')}
        count={items.length}
        actions={isEditMode && !invoiceLinesLocked ? (
          <>
            {canEditPurchasePrices && (
              <PZBatchConversionMenu onConvert={handleBatchConversion} disabled={items.length === 0} />
            )}
            <Button size="small" icon={<PlusOutlined />} onClick={addItem}>
              {t('pz:actions.addItem')}
            </Button>
          </>
        ) : undefined}
      >
        <PZItemsSection
          items={items}
          isEditMode={isEditMode && !invoiceLinesLocked}
          canViewPurchasePrices={canViewPZPurchasePrices}
          canEditPurchasePrices={canEditPurchasePrices}
          canEditQuantityReceived={canEditQuantityReceived}
          documentReceived={isCorrection}
          receiptQuantityOnly={receiptQuantityOnly}
          quantityTotalsLabel={totalQuantity}
          totalValue={totalValue}
          isInvoiceReceipt={Boolean(pzDocument?.purchaseInvoiceId)}
          acceptedReceiptCostPLN={pzDocument?.acceptedReceiptCostPLN}
          currentBatchValuePLN={pzDocument?.currentBatchValuePLN}
          onItemChange={updateItem}
          onQuantityChange={updateItemQuantity}
          onVariantSelect={handleVariantSelect}
          onRemoveItem={removeItem}
        />
        {itemValidationError ? (
          <Alert className="pz-card__items-error" type="error" showIcon message={itemValidationError} />
        ) : null}
      </ObjectCardSection>
    ),
  };

  const stockValuationSlot: CardSlot | null = stockValuation && canViewPZPurchasePrices ? {
    key: 'stock-valuation',
    render: (wide) => (
      <ObjectCardSection
        wide={wide}
        testId="pz-section-stock-valuation"
        tone="orange"
        icon={<CalculatorOutlined />}
        title={t('pz:stockValuation.title')}
        actions={(
          <span className={`obj-pill ${stockValuation.valuationComplete ? 'obj-pill--success' : 'obj-pill--warning'}`}>
            {t(stockValuation.valuationComplete ? 'pz:stockValuation.complete' : 'pz:stockValuation.incomplete')}
          </span>
        )}
      >
        {/* Owner 2026-10-12: label next to value, so amounts never wrap per character. */}
        <ObjectCardFacts
          items={[
            { label: t('pz:stockValuation.extraTotalPLN'), value: formatCurrency(stockValuation.extraTotalPLN, 'PLN') },
            {
              label: t('pz:stockValuation.allocationUnit'),
              value: stockValuation.allocationUnit
                ? formatUnit(stockValuation.allocationUnit)
                : t('pz:stockValuation.allocationUnitUnresolved'),
            },
            {
              label: t('pz:stockValuation.freightPLN'),
              value: (
                <>
                  {stockValuation.freightPLN == null ? EMPTY_VALUE : formatCurrency(stockValuation.freightPLN, 'PLN')}
                  {/* Owner 2026-10-12: say where the freight comes from. */}
                  {stockValuation.freightSource === 'TRUCK' ? (
                    <span className="pz-card__freight-source" data-testid="pz-freight-source">
                      {' · '}{t('pz:stockValuation.fromTruck', { trucks: (stockValuation.freightTruckLabels || []).join(', ') })}
                    </span>
                  ) : stockValuation.freightSource === 'AVERAGED' ? (
                    <span className="pz-card__freight-source" data-testid="pz-freight-source">
                      {' · '}{t('pz:stockValuation.averaged')}
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              label: t('pz:stockValuation.landedPLN'),
              value: stockValuation.landedPLN == null ? EMPTY_VALUE : formatCurrency(stockValuation.landedPLN, 'PLN'),
            },
          ]}
        />
      </ObjectCardSection>
    ),
  } : null;

  // Etap 2 (transport przywozowy): which truck this receipt came off.
  // Read-only once the document is RECEIVED/CANCELLED — the stock is
  // already booked by then and the trace must not move under it.
  const deliverySlot: CardSlot | null = canEditManualDeliveryTransports ? {
    key: 'manual-delivery',
    render: (wide) => (
      <ObjectCardSection wide={wide} tone="teal" icon={<CarOutlined />} title={t('pz:manualReceipt.transportTitle')}>
        <ManualPZTransportPicker value={sourceTransportIds} onChange={setSourceTransportIds} />
      </ObjectCardSection>
    ),
  } : showDeliveryTransportsSection ? {
    key: 'delivery',
    render: (wide) => (
      <ObjectCardSection
        wide={wide}
        testId="pz-delivery-transports-section"
        tone="teal"
        icon={<CarOutlined />}
        title={t('pz:deliveryTransports.title')}
        hint={canEditDeliveryTransports
          ? t('pz:deliveryTransports.editHint')
          : t('pz:deliveryTransports.viewHint')}
      >
        <DeliveryTransportPicker
          transports={deliveryTransportOptions}
          value={sourceTransportIds}
          onChange={canEditDeliveryTransports ? setSourceTransportIds : undefined}
          readOnly={!canEditDeliveryTransports}
          hideNoTransportOption
          loading={supplierOrderTransportsLoading}
          loadFailed={supplierOrderTransportsFailed && deliveryTransportOptions.length === 0}
          onRetry={() => { void loadSupplierOrderTransports(); }}
          data-testid="pz-delivery-transport-picker"
        />
        {/* WP12 (1.18): any transport may be named; one not assigned to
            this ZD is assigned after a confirmed warning (its freight
            then enters the ZD's batches). */}
        {canEditDeliveryTransports && sourceSupplierOrderId ? (
          <div className="pz-card__other-transport">
            <ManualPZTransportPicker
              value={sourceTransportIds}
              onChange={setSourceTransportIds}
              title={t('pz:deliveryTransports.otherTransportTitle')}
              hint={t('pz:deliveryTransports.otherTransportHint')}
            />
          </div>
        ) : null}
      </ObjectCardSection>
    ),
  } : null;

  const viewSlots: Array<CardSlot | null> = [
    itemsSection,
    stockValuationSlot,
    {
      key: 'supplier',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="pz-section-supplier" tone="indigo" icon={<ShopOutlined />} title={t('pz:sections.supplier')}>
          <ObjectCardFacts
            items={[
              { label: t('pz:fields.supplierNIP'), value: pzDocument?.supplierNIP || EMPTY_VALUE },
              { label: t('pz:fields.supplierAddress'), value: pzDocument?.supplierAddress || EMPTY_VALUE },
              ...(canViewPZPurchasePrices ? [
                {
                  label: t('pz:fields.supplierInvoiceDate'),
                  value: pzDocument?.supplierInvoiceDate ? formatDate(pzDocument.supplierInvoiceDate) : EMPTY_VALUE,
                },
                {
                  label: t('pz:fields.supplierInvoiceAmount'),
                  value: pzDocument?.supplierInvoiceAmount != null
                    ? formatCurrency(pzDocument.supplierInvoiceAmount, pzDocument.supplierInvoiceCurrency || pzDocument.currency || 'PLN')
                    : EMPTY_VALUE,
                },
              ] : []),
            ]}
          />
        </ObjectCardSection>
      ),
    },
    {
      key: 'signature',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="pz-section-signature" tone="gray" icon={<UserOutlined />} title={t('pz:sections.signature')}>
          <ObjectCardFacts
            items={[
              { label: t('pz:fields.approvedBy'), value: pzDocument?.approvedBy || EMPTY_VALUE },
              { label: t('pz:fields.receivedBy'), value: pzDocument?.receivedBy || EMPTY_VALUE },
              { label: t('pz:fields.notes'), value: pzDocument?.notes || EMPTY_VALUE },
            ]}
          />
        </ObjectCardSection>
      ),
    },
    deliverySlot,
    isCorrection && id ? {
      key: 'corrections',
      wide: true,
      render: () => <WarehouseCorrectionPanel documentId={id} documentType="PZ" editing={false} version={pzDocument?.updatedAt} />,
    } : null,
  ];

  const editSlots: Array<CardSlot | null> = [
    isCorrection && id ? {
      key: 'correction',
      wide: true,
      render: () => <WarehouseCorrectionPanel documentId={id} documentType="PZ" editing version={pzDocument?.updatedAt} />,
    } : null,
    {
      key: 'basic',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="pz-section-basic" tone="blue" icon={<FileTextOutlined />} title={t('pz:sections.basicInfo')}>
          <div className="obj-form">
            <Form.Item label={t('pz:fields.pzNumber')} name="pzNumber">
              <Input maxLength={50} placeholder={t('pz:placeholders.pzNumber')} />
            </Form.Item>
            <Form.Item
              label={t('pz:fields.issueDate')}
              name="issueDate"
              rules={[{ required: true, message: t('pz:validation.issueDateRequired') }]}
            >
              <DatePicker className="doc-control" format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item
              label={t('pz:fields.receiptDate')}
              name="receiptDate"
              rules={[{ required: true, message: t('pz:validation.receiptDateRequired') }]}
            >
              <DatePicker className="doc-control" format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item
              label={t('pz:fields.currency')}
              name="currency"
              rules={[{ required: true, message: t('pz:validation.currencyRequired') }]}
            >
              <Select>
                <Option value="PLN">PLN</Option>
                <Option value="USD">USD</Option>
                <Option value="EUR">EUR</Option>
              </Select>
            </Form.Item>
          </div>
        </ObjectCardSection>
      ),
    },
    {
      key: 'supplier',
      render: (wide) => (
        <ObjectCardSection
          wide={wide}
          testId="pz-section-supplier"
          tone="indigo"
          icon={<ShopOutlined />}
          title={t('pz:sections.supplier')}
          hint={t('pz:fields.supplierDerivedHint')}
        >
          <div className="obj-form">
            <Form.Item
              label={t('pz:fields.supplier')}
              name="supplierId"
              rules={[{ required: true, message: t('pz:validation.supplierRequired') }]}
            >
              <Select
                placeholder={t('pz:actions.selectSupplier')}
                onChange={handleSupplierChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {suppliers.map(supplier => (
                  <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          {/* Read-only on purpose: the backend re-derives both from the
              Supplier record on every save, so an edit here was accepted
              by the form and then silently discarded. They show what will
              be printed; the place to change them is the supplier card. */}
          <Form.Item noStyle shouldUpdate>
            {() => (
              <ObjectCardFacts
                items={[
                  { label: t('pz:fields.supplierNIP'), value: form.getFieldValue('supplierNIP') || EMPTY_VALUE },
                  { label: t('pz:fields.supplierAddress'), value: form.getFieldValue('supplierAddress') || EMPTY_VALUE },
                ]}
              />
            )}
          </Form.Item>
        </ObjectCardSection>
      ),
    },
    canViewPZPurchasePrices ? {
      key: 'supplier-invoice',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="pz-section-supplier-invoice" tone="orange" icon={<FileTextOutlined />} title={t('pz:sections.supplierInvoice')}>
          <div className="obj-form">
            <Form.Item label={t('pz:fields.supplierInvoiceNumber')} name="supplierInvoiceNumber">
              <Input placeholder={t('pz:placeholders.supplierInvoiceNumber')} />
            </Form.Item>
            <Form.Item label={t('pz:fields.supplierInvoiceDate')} name="supplierInvoiceDate">
              <DatePicker className="doc-control" format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item label={t('pz:fields.supplierInvoiceAmount')} name="supplierInvoiceAmount">
              <InputNumber
                className="doc-control"
                min={0}
                step={0.01}
                placeholder={t('pz:placeholders.supplierInvoiceAmount')}
                {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
              />
            </Form.Item>
            <Form.Item label={t('pz:fields.supplierInvoiceCurrency')} name="supplierInvoiceCurrency">
              <Select>
                <Option value="PLN">PLN</Option>
                <Option value="USD">USD</Option>
                <Option value="EUR">EUR</Option>
              </Select>
            </Form.Item>
          </div>
        </ObjectCardSection>
      ),
    } : null,
    {
      key: 'signature',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="pz-section-signature" tone="gray" icon={<UserOutlined />} title={t('pz:sections.signature')}>
          <div className="obj-form">
            <Form.Item
              label={t('pz:fields.approvedBy')}
              name="approvedBy"
              rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
            >
              <Input placeholder={t('pz:placeholders.approvedBy')} />
            </Form.Item>
            <Form.Item
              label={t('pz:fields.receivedBy')}
              name="receivedBy"
              rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
            >
              <Input placeholder={t('pz:placeholders.receivedBy')} />
            </Form.Item>
            <Form.Item name="notes" label={t('pz:fields.notes')} className="doc-form-stacked">
              <TextArea rows={2} placeholder={t('pz:placeholders.notes')} />
            </Form.Item>
          </div>
        </ObjectCardSection>
      ),
    },
    itemsSection,
    stockValuationSlot,
    deliverySlot,
  ];

  return (
    <div className={`obj-card doc-card pz-card${isStorno ? ' pz-card--storno' : ''}`} data-testid="pz-card">
      <Form
        className="pz-card__form"
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onValuesChange={handleFormValuesChange}
        onFinishFailed={() => feedback.warning(t('pz:fillRequired'))}
        // Live QA (agent INDIA): Enter in a single-line field (the receipt-date
        // picker, in the reported case) implicit-submitted this form. Today
        // validation happens to catch it; the guard makes that not a matter of
        // luck. Same rule as the WZ and transport forms.
        onKeyDown={(event) => {
          if (shouldPreventEnterFormSubmit(event)) {
            event.preventDefault();
          }
        }}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          receiptDate: dayjs(),
          currency: 'PLN',
          supplierInvoiceCurrency: 'PLN',
          approvedBy: currentUserDisplayName,
          receivedBy: currentUserDisplayName,
        }}
      >
        <ObjectCardHero
          testId="pz-card-header"
          kind={t('pz:card.kind')}
          name={pzDocument?.pzNumber || ''}
          avatar={<InboxOutlined />}
          backLabel={t('pz:card.back')}
          onBack={() => goBack('/app/pz')}
          badges={pzDocument?.status ? (
            <span className={`obj-pill ${statusPillClass}`.trim()} data-testid="pz-status-pill">
              {isStorno
                ? t('pz:storno.tag')
                : t(`pz:status.${getPZOperationalStatus(pzDocument.status).toLowerCase()}`)}
            </span>
          ) : undefined}
          meta={isStorno ? [
            t('pz:storno.annotation', {
              reason: pzDocument?.stornoReason || '',
              who: pzDocument?.stornoByName || t('pz:storno.unknownUser'),
              when: pzDocument?.stornoAt ? dayjs(pzDocument.stornoAt).format('DD.MM.YYYY HH:mm') : '',
            }),
          ] : [pzDocument?.supplierAddress]}
          actions={(moreItems.length > 0 || (!isEditMode && canEdit)) ? (
            <>
              {moreItems.length > 0 && (
                <Dropdown
                  menu={{ items: moreItems, onClick: ({ key }) => { if (key === 'pdf') void handleDownloadPDF(); } }}
                  trigger={['click']}
                >
                  <Button icon={<MoreOutlined />} data-testid="pz-more-actions">
                    {t('pz:card.more')}
                  </Button>
                </Dropdown>
              )}
              {!isEditMode && canEdit && !(invoiceBacked && isCorrection) && (
                <Tooltip title={blockedTooltip('edit')}>
                  <Button
                    icon={<EditOutlined />}
                    disabled={Boolean(blockedFor('edit'))}
                    onClick={() => navigate(`/app/pz/${id}?mode=edit`, { replace: true })}
                  >
                    {t('common:edit')}
                  </Button>
                </Tooltip>
              )}
              {!isEditMode && canEdit && invoiceBacked && isCorrection && (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/app/pz/${id}?mode=correction`, { replace: true })}>
                  {t('pz:correction.create')}
                </Button>
              )}
            </>
          ) : undefined}
          facts={facts}
          alerts={headerAlerts.length > 0 ? headerAlerts : undefined}
        />

        <div className="obj-card__body">
          {layoutCardSlots(isEditMode ? editSlots : viewSlots)}
        </div>

        {isEditMode ? (
          <div className="doc-bar">
            <div className="doc-bar__actions">
              <Button onClick={() => navigate('/app/pz')}>{t('common:cancel')}</Button>
              <Button type="primary" loading={saving} icon={<SaveOutlined />} htmlType="submit">
                {t('common:save')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Why a button in the operations bar below is disabled: next to it, not in the header. */}
            {canEdit && blockedActions.length > 0 && (
              <BlockedActionsAlert blockedActions={blockedActions} ns="pz" reasonKeys={PZ_ERROR_CODE_KEYS} />
            )}
            {renderStatusActions()}
          </>
        )}
      </Form>

      {pzDocument && id && (
        <PZStornoDialog
          open={stornoDialogOpen}
          pzId={id}
          pzNumber={pzDocument.pzNumber}
          expectedUpdatedAt={pzDocument.updatedAt}
          preview={stornoPreview}
          onCancel={() => setStornoDialogOpen(false)}
          onDone={async () => {
            setStornoDialogOpen(false);
            await fetchPZDocument();
            feedback.success(t('pz:success.storno', { pzNumber: pzDocument.pzNumber }));
          }}
        />
      )}
    </div>
  );
};

export default PZDetail;
